import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';
import { Input } from '../../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Plus, Trash2 } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import type { SpecificationDocument } from '../../../types/index';
import { DocumentTag } from '../../../types';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { getListMaterialCategory, AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { bulkCreateUpdateWelderQualification, getPreSignedUrlToUpload } from '../../../../../redux/Project/registrySaga';
import { getAllPipeDetails } from '../../../../../redux/Project/setupData/action';

const QUALIFICATION_OPTIONS = [
  { value: '-', label: '— (Not qualified)' },
  { value: 'WPQT', label: 'WPQT' },
  { value: 'WQT XX', label: 'WQT XX' },
  { value: 'WPS XX', label: 'WPS XX' },
] as const;

function transformDocumentsToSpecifications(setupData: Record<string, any>): SpecificationDocument[] {
  const specifications: SpecificationDocument[] = [];
  Object.values(setupData || {}).forEach((setup: any) => {
    (setup.pipeSizes || []).forEach((pipe: any) => {
      const docs = pipe.documentsData?.documents;
      if (!Array.isArray(docs)) return;
      docs.forEach((doc: any) => {
        specifications.push({
          id: doc.docRefId?.toString(),
          fileName: doc.fileName || '',
          type: doc.type as SpecificationDocument['type'],
          pipeId: pipe.pipeId?.toString(),
          projectId: setup.projectId,
          projectTypeId: setup.projectTypeId,
          pipeOrder: pipe.pipeOrder || 0,
          fileUrl: doc.fileUrl,
          s3Key: doc.s3Key,
          uploadedBy: doc.uploadedBy,
          uploadDate: doc.uploadDate,
          version: doc.version || 1,
          details: doc.details || {},
          files: doc.files || [],
          docRefId: doc.docRefId || 0,
        });
      });
    });
  });
  return specifications;
}

function formatPipeRowLabel(pipe: {
  pipeName?: string;
  size?: number | string;
  wallThickness?: number | string;
  projectType?: string;
  projectSubType?: string;
}): string {
  const nm = pipe.pipeName || 'Pipe';
  const sz = pipe.size ?? '';
  const wt = pipe.wallThickness ?? '';
  return `${nm} (${sz} × ${wt})`;
}

function extractPipeRowsFromDetails(setupData: Record<string, any>): Array<{
  pipeId: string | number;
  projectTypeId: string | number;
  label: string;
}> {
  const rows: Array<{ pipeId: string | number; projectTypeId: string | number; label: string }> = [];
  Object.values(setupData || {}).forEach((setup: any) => {
    (setup.pipeSizes || []).forEach((pipe: any) => {
      rows.push({
        pipeId: pipe.pipeId,
        projectTypeId: setup.projectTypeId,
        label: formatPipeRowLabel({
          pipeName: pipe.pipeName,
          size: pipe.pipeSize ?? pipe.size,
          wallThickness: pipe.wallThickness,
          projectType: setup.projectType,
          projectSubType: setup.projectSubType,
        }),
      });
    });
  });
  return rows;
}

/** Matches `WpsColumnDef` on the register table (kept local to avoid circular imports). */
export interface WelderQualificationWpsColumn {
  id: string;
  wpsLabel: string;
  od: string;
  wt: string;
  scope: string;
}

export interface WelderQualificationRowDocument {
  fileName: string;
  s3Key: string;
  tags?: string[];
  uploadedBy?: string;
}

/** One pipe size + WPS specification pair (repeatable in the modal). */
export interface WelderPipeWpsLine {
  pipeSizeDisplay?: string;
  pipeId?: string | null;
  projectTypeId?: string | null;
  wpsSpecificationId?: string | null;
  wpsSpecificationLabel?: string | null;
  /** @deprecated Legacy single field; migrated to scopeOfWorkType when loading old rows. */
  qualificationType?: string | null;
  scopeOfWorkType?: string | null;
  workType?: string | null;
}

type PipeWpsFormRow = WelderPipeWpsLine & { key: string };
type TeamWelderOption = { id: string; fullName: string; user_id: number | null };

/** Same duplicate rule as submit: name + id when both have id; otherwise name-only. */
function welderOptionOccupiesRegisterRow(
  fullName: string,
  userId: number | null,
  row: WelderQualificationRowForEdit
): boolean {
  const nameMatch =
    row.welderName.trim().toLowerCase() === fullName.trim().toLowerCase();
  const rid = row.welderId != null ? Number(row.welderId) : null;
  if (rid != null && userId != null) return rid === userId && nameMatch;
  return nameMatch;
}

function newPipeWpsFormRow(): PipeWpsFormRow {
  return {
    key: `pw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    pipeSizeDisplay: '',
    pipeId: null,
    projectTypeId: null,
    wpsSpecificationId: null,
    wpsSpecificationLabel: null,
    qualificationType: null,
    scopeOfWorkType: null,
    workType: null,
  };
}

export interface WelderQualificationNewRowPayload {
  project_id: number;
  weldNumber: string;
  welderName: string;
  welderId: number | null;
  operatorUserId: number | null;
  notes?: string;
  documents?: WelderQualificationRowDocument[];
  /** Per–WPS-column qualification codes (column id → code). */
  qualifications?: Record<string, string>;
  /** Multiple pipe / WPS lines when user adds more than one. */
  pipeWpsLines?: WelderPipeWpsLine[];
  /** First line or joined summary for backward compatibility / search. */
  pipeSizeDisplay?: string;
  pipeId?: string | null;
  /** First line project type (summary). */
  projectTypeId?: string | null;
  wpsSpecificationId?: string | null;
  wpsSpecificationLabel?: string | null;
}

export interface WelderQualificationRowForEdit {
  id: string;
  weldNumber: string;
  welderName: string;
  welderId: number | null;
  operatorUserId: number | null;
  notes?: string;
  documents?: WelderQualificationRowDocument[];
  qualifications?: Record<string, string>;
  pipeWpsLines?: WelderPipeWpsLine[];
  pipeSizeDisplay?: string;
  pipeId?: string | null;
  projectTypeId?: string | null;
  wpsSpecificationId?: string | null;
  wpsSpecificationLabel?: string | null;
}

export interface AddWelderQualificationRowModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional hook after a successful API save (e.g. refetch table). */
  onSaved?: () => void | Promise<void>;
  welderRowToEdit?: WelderQualificationRowForEdit | null;
  qualificationList?: WelderQualificationRowForEdit[];
  project: DashboardProject;
  wpsColumns: WelderQualificationWpsColumn[];
  customWelderNames: string[];
  onAddCustomWelderName: (name: string) => void;
}



export const AddWelderQualificationRowModal: React.FC<AddWelderQualificationRowModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  welderRowToEdit = null,
  qualificationList = [],
  project,
  wpsColumns,
  customWelderNames,
  onAddCustomWelderName,
}) => {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { pipeDetails } = useSelector((state: any) => state.setupData || {});

  const [weldNumber, setWeldNumber] = useState('');
  const [welderName, setWelderName] = useState('');
  const [welderId, setWelderId] = useState<number | null>(null);
  const [operatorUserId, setOperatorUserId] = useState<number | null>(null);
  const [qualifications, setQualifications] = useState<Record<string, string>>({});
  const [isAddingWelderName, setIsAddingWelderName] = useState(false);
  const [notes, setNotes] = useState('');
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState<WelderQualificationRowDocument[]>([]);
  const [pipeWpsRows, setPipeWpsRows] = useState<PipeWpsFormRow[]>(() => [newPipeWpsFormRow()]);
  const [scopeOfWorkTags, setScopeOfWorkTags] = useState<any[]>([]);
  const [workTypeTags, setWorkTypeTags] = useState<any[]>([]);
  const [isAddingScopeOfWorkType, setIsAddingScopeOfWorkType] = useState(false);
  const [isAddingWorkType, setIsAddingWorkType] = useState(false);

  const isEditMode = Boolean(welderRowToEdit);

  const { teamMembers, loading: teamMembersLoading } = useTeamMembersReduxHook(
    undefined,
    Number(projectId)
  );

  useEffect(() => {
    if (!isOpen || !projectId) return;
    const matchesProject =
      pipeDetails &&
      Object.keys(pipeDetails).length > 0 &&
      Object.values(pipeDetails).some(
        (setup: any) => setup?.projectId && String(setup.projectId) === String(projectId)
      );
    if (!pipeDetails || Object.keys(pipeDetails).length === 0 || !matchesProject) {
      dispatch(getAllPipeDetails({ projectId }));
    }
  }, [isOpen, projectId, dispatch, pipeDetails]);

  const specifications = useMemo(() => {
    if (!pipeDetails || Object.keys(pipeDetails).length === 0) return [];
    return transformDocumentsToSpecifications(pipeDetails as Record<string, any>);
  }, [pipeDetails]);

  const pipeRows = useMemo(() => {
    if (!pipeDetails || Object.keys(pipeDetails).length === 0) return [];
    return extractPipeRowsFromDetails(pipeDetails as Record<string, any>);
  }, [pipeDetails]);

  const pipeSizeOptions = useMemo(
    () =>
      pipeRows.map((r, i) => ({
        id: `pipe-${r.projectTypeId}-${r.pipeId}-${i}`,
        name: r.label,
      })),
    [pipeRows]
  );

  const buildWpsOptionsForPipe = useCallback(
    (pipeId: string | null | undefined) => {
      const wpsList = specifications.filter((spec) => spec.type === 'WPS');
      const filtered =
        pipeId == null || pipeId === ''
          ? wpsList
          : wpsList.filter((s) => !s.pipeId || String(s.pipeId) === String(pipeId));
      return filtered.map((wps) => ({
        id: String(wps.id),
        name:
          wps.files?.[wps.files.length - 1]?.fileName ||
          wps.fileName ||
          `WPS ${wps.id}`,
      }));
    },
    [specifications]
  );

  const teamWelderDirectory = useMemo(() => {
    const members = (teamMembers || [])
      .map((member: any) => {
        const u = member.apiData?.user || member.user;
        const fallbackUserId = member.user_id ?? member.apiData?.user_id ?? member.userId ?? null;
        const fullNameFromUser = `${u?.first_name || ''} ${u?.last_name || ''}`.trim();
        const fullName = fullNameFromUser || member.name || '';
        if (!fullName) return null;
        const resolvedUserId = u?.user_id ?? fallbackUserId ?? null;
        return {
          id: `team-${resolvedUserId ?? fullName}`,
          fullName,
          user_id: resolvedUserId,
        };
      })
      .filter((o: TeamWelderOption | null): o is TeamWelderOption => o != null);

    const seen = new Set<string>();
    return members.filter((m: TeamWelderOption) => {
      const key = `${m.fullName.toLowerCase()}::${m.user_id ?? 'na'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [teamMembers]);

  const occupiedRegisterRows = useMemo(
    () =>
      qualificationList.filter(
        (r) => !isEditMode || !welderRowToEdit || String(r.id) !== String(welderRowToEdit.id)
      ),
    [qualificationList, isEditMode, welderRowToEdit]
  );

  const welderNameOptions = useMemo(() => {
    const teamOpts = teamWelderDirectory
      .filter(
        (m: TeamWelderOption) =>
          !occupiedRegisterRows.some((r: WelderQualificationRowForEdit) =>
            welderOptionOccupiesRegisterRow(m.fullName, m.user_id, r)
          )
      )
      .map((m: TeamWelderOption) => ({
        id: m.id,
        name: m.fullName.trim(),
      }));

    const teamNameSet = new Set(
      teamWelderDirectory.map((m: TeamWelderOption) => m.fullName.toLowerCase())
    );
    const custom = customWelderNames
      .filter((name) => !teamNameSet.has(name.trim().toLowerCase()))
      .filter(
        (name) =>
          !occupiedRegisterRows.some(
            (r) => r.welderName.trim().toLowerCase() === name.trim().toLowerCase()
          )
      )
      .map((name, i) => ({ id: `custom-${i}`, name: name.trim() }));

    let merged = [...teamOpts, ...custom].sort((a, b) => a.name.localeCompare(b.name));

    const wn = welderName.trim();
    if (isEditMode && wn) {
      const hasExact = merged.some((o) => o.name === wn);
      if (!hasExact) {
        merged = [{ id: `current-edit-${welderRowToEdit?.id ?? 'row'}`, name: wn }, ...merged];
      }
    }

    return merged;
  }, [
    teamWelderDirectory,
    customWelderNames,
    occupiedRegisterRows,
    isEditMode,
    welderName,
    welderRowToEdit?.id,
  ]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (tags && tags.length > 0) {
          const consumableTags = tags.filter(
            (tag: any) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS'
          );
          setDocumentTags(consumableTags);
          setScopeOfWorkTags(
            tags.filter((tag: any) => tag.tags_type === 'WELDER_SCOPE_OF_WORK_TYPE')
          );
          setWorkTypeTags(tags.filter((tag: any) => tag.tags_type === 'WELDER_WORK_TYPE'));
        }
      } catch (error) {
        console.error('Failed to load document tags:', error);
      }
    };
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  const tagOptions = useMemo(
    () =>
      documentTags.map((tag) => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name,
      })),
    [documentTags]
  );

  const scopeOfWorkTypeOptions = useMemo(() => {
    const defaultOptions = [
      { id: 'def-ba', name: 'Buckle Arrestor' },
      { id: 'def-tj', name: 'Transition Joint Assembly' },
      { id: 'def-tf', name: 'Gas Export PLET Transition Forging' },
      { id: 'def-general', name: 'General pipeline' },
    ];
    const dbOptions = scopeOfWorkTags
      .map((tag: any) => ({
        id: tag.tags_id?.toString() || '',
        name: tag.tags_name || tag.name || '',
      }))
      .filter((opt: { id: string; name: string }) => opt.id && opt.name);
    const allOptions = [...dbOptions];
    const dbNames = new Set(dbOptions.map((opt: { name: string }) => opt.name.toLowerCase()));
    defaultOptions.forEach((d) => {
      if (!dbNames.has(d.name.toLowerCase())) allOptions.push(d);
    });
    return allOptions.length > 0 ? allOptions : defaultOptions;
  }, [scopeOfWorkTags]);

  const workTypeOptions = useMemo(() => {
    const defaultOptions = [
      { id: 'def-field', name: 'Field welding' },
      { id: 'def-shop', name: 'Shop welding' },
      { id: 'def-repair', name: 'Repair / overlay' },
      { id: 'def-automatic', name: 'Automatic / semi-automatic' },
      { id: 'def-manual', name: 'Manual' },
    ];
    const dbOptions = workTypeTags
      .map((tag: any) => ({
        id: tag.tags_id?.toString() || '',
        name: tag.tags_name || tag.name || '',
      }))
      .filter((opt: { id: string; name: string }) => opt.id && opt.name);
    const allOptions = [...dbOptions];
    const dbNames = new Set(dbOptions.map((opt: { name: string }) => opt.name.toLowerCase()));
    defaultOptions.forEach((d) => {
      if (!dbNames.has(d.name.toLowerCase())) allOptions.push(d);
    });
    return allOptions.length > 0 ? allOptions : defaultOptions;
  }, [workTypeTags]);

  const resolvePipeFromLabel = useCallback(
    (label: string) => {
      const row = pipeRows.find((r) => r.label === label);
      return row
        ? { pipeId: String(row.pipeId), projectTypeId: String(row.projectTypeId) }
        : { pipeId: null as string | null, projectTypeId: null as string | null };
    },
    [pipeRows]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (welderRowToEdit) {
      setWeldNumber(welderRowToEdit.weldNumber || '');
      setWelderName(welderRowToEdit.welderName || '');
      setWelderId(welderRowToEdit.welderId ?? null);
      setOperatorUserId(welderRowToEdit.operatorUserId ?? null);
      setNotes(welderRowToEdit.notes || '');
      setMaterialDocuments([]);
      setSelectedTags([]);
      setExistingDocuments(welderRowToEdit.documents?.length ? [...welderRowToEdit.documents] : []);
      const lines = welderRowToEdit.pipeWpsLines;
      const qualInit: Record<string, string> = {};
      wpsColumns.forEach((c) => {
        qualInit[c.id] = welderRowToEdit.qualifications?.[c.id] ?? '-';
      });
      setQualifications(qualInit);

      if (lines && lines.length > 0) {
        setPipeWpsRows(
          lines.map((l, i) => ({
            key: `pw-edit-${welderRowToEdit.id}-${i}`,
            pipeSizeDisplay: l.pipeSizeDisplay || '',
            pipeId: l.pipeId ?? null,
            projectTypeId: l.projectTypeId ?? null,
            wpsSpecificationLabel: l.wpsSpecificationLabel || null,
            wpsSpecificationId: l.wpsSpecificationId ?? null,
            qualificationType: l.qualificationType ?? null,
            scopeOfWorkType: l.scopeOfWorkType ?? (l.qualificationType && !l.workType ? l.qualificationType : null),
            workType: l.workType ?? null,
          }))
        );
      } else {
        setPipeWpsRows([
          {
            key: `pw-edit-${welderRowToEdit.id}-0`,
            pipeSizeDisplay: welderRowToEdit.pipeSizeDisplay || '',
            pipeId: welderRowToEdit.pipeId ?? null,
            projectTypeId: welderRowToEdit.projectTypeId ?? null,
            wpsSpecificationLabel: welderRowToEdit.wpsSpecificationLabel || null,
            wpsSpecificationId: welderRowToEdit.wpsSpecificationId ?? null,
            qualificationType: null,
            scopeOfWorkType: null,
            workType: null,
          },
        ]);
      }
    } else {
      setWeldNumber('');
      setWelderName('');
      setWelderId(null);
      setOperatorUserId(null);
      setNotes('');
      setMaterialDocuments([]);
      setSelectedTags([]);
      setExistingDocuments([]);
      setPipeWpsRows([newPipeWpsFormRow()]);
      const initial: Record<string, string> = {};
      wpsColumns.forEach((c) => {
        initial[c.id] = '-';
      });
      setQualifications(initial);
    }
  }, [isOpen, welderRowToEdit, wpsColumns]);

  const handleAddPipeWpsRow = () => {
    setPipeWpsRows((prev) => [...prev, newPipeWpsFormRow()]);
  };

  const handleRemovePipeWpsRow = (index: number) => {
    if (pipeWpsRows.length <= 1) {
      customNotification.warning('At least one pipe / WPS line is required.');
      return;
    }
    setPipeWpsRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePipeWpsRow = (index: number, patch: Partial<WelderPipeWpsLine>) => {
    setPipeWpsRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handlePipeSizeSelectAt = (index: number, label: string) => {
    const { pipeId, projectTypeId } = resolvePipeFromLabel(label);
    updatePipeWpsRow(index, {
      pipeSizeDisplay: label,
      pipeId,
      projectTypeId,
      wpsSpecificationLabel: null,
      wpsSpecificationId: null,
    });
  };

  const handleWpsSpecSelectAt = (index: number, pipeId: string | null | undefined, name: string | undefined) => {
    const label = name ?? '';
    const opts = buildWpsOptionsForPipe(pipeId ?? null);
    const opt = opts.find((o) => o.name === label);
    updatePipeWpsRow(index, {
      wpsSpecificationLabel: label,
      wpsSpecificationId: opt?.id ?? null,
    });
  };

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setMaterialDocuments((prev) => [...prev, ...fileArray]);
      const newTags = Array(fileArray.length).fill([]) as string[][];
      setSelectedTags((prev) => [...prev, ...newTags]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updated = [...selectedTags];
    updated[fileIndex] = tags;
    setSelectedTags(updated);
  };

  const reloadWelderTagLists = async () => {
    try {
      const tags = await getListMaterialCategory();
      if (tags && tags.length > 0) {
        setScopeOfWorkTags(tags.filter((tag: any) => tag.tags_type === 'WELDER_SCOPE_OF_WORK_TYPE'));
        setWorkTypeTags(tags.filter((tag: any) => tag.tags_type === 'WELDER_WORK_TYPE'));
      }
    } catch {
      /* ignore */
    }
  };

  const handleAddScopeOfWorkType = async (typeName: string) => {
    if (!typeName.trim() || !projectId) return;
    if (
      scopeOfWorkTypeOptions.some((opt) => opt.name.toLowerCase() === typeName.trim().toLowerCase())
    ) {
      customNotification.warning(`${typeName.trim()} already exists in the list.`);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: typeName.trim(),
        project_id: Number(projectId),
        tags_type: 'WELDER_SCOPE_OF_WORK_TYPE',
        created_by: user.email || '',
      },
      'Scope of work type'
    );
    if (res) {
      setScopeOfWorkTags((prev) =>
        prev.some((t: any) => String(t.tags_id) === String(res.tags_id)) ? prev : [...prev, res]
      );
      void reloadWelderTagLists();
      setIsAddingScopeOfWorkType(false);
    }
  };

  const handleAddWorkType = async (typeName: string) => {
    if (!typeName.trim() || !projectId) return;
    if (workTypeOptions.some((opt) => opt.name.toLowerCase() === typeName.trim().toLowerCase())) {
      customNotification.warning(`${typeName.trim()} already exists in the list.`);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: typeName.trim(),
        project_id: Number(projectId),
        tags_type: 'WELDER_WORK_TYPE',
        created_by: user.email || '',
      },
      'Work type'
    );
    if (res) {
      setWorkTypeTags((prev) =>
        prev.some((t: any) => String(t.tags_id) === String(res.tags_id)) ? prev : [...prev, res]
      );
      void reloadWelderTagLists();
      setIsAddingWorkType(false);
    }
  };

  const handleAddTag = async (tagName: string) => {
    if (!tagName.trim() || !projectId) return;
    const existingTag = documentTags.find(
      (tag: any) => tag.tags_name.toLowerCase() === tagName.trim().toLowerCase()
    );
    if (existingTag) {
      customNotification.warning(`${tagName.trim()} tag already exists`);
      return;
    }

    const apiPayload = {
      tags_name: tagName.trim(),
      project_id: Number(projectId),
      tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
      created_by: user.email || '',
    };
    const res = await AddMaterialCategory(apiPayload, 'Document Tag');
    if (res) {
      setDocumentTags((prev) => [...prev, res]);
      setIsAddingTag(false);
    }
  };

  const handleWelderSelect = (value: string) => {
    const selectedMember = teamWelderDirectory.find(
      (m: TeamWelderOption) => m.fullName.trim() === value.trim()
    );

    if (selectedMember) {
      setWelderName(selectedMember.fullName.trim());
      setWelderId(selectedMember.user_id ?? null);
      setOperatorUserId(selectedMember.user_id ?? null);
    } else {
      setWelderName(value);
      const matchesCurrentEdit =
        isEditMode &&
        welderRowToEdit &&
        value.trim().toLowerCase() === (welderRowToEdit.welderName || '').trim().toLowerCase();
      if (matchesCurrentEdit) {
        setWelderId(welderRowToEdit.welderId ?? null);
        setOperatorUserId(welderRowToEdit.operatorUserId ?? null);
      } else {
        setWelderId(null);
        setOperatorUserId(null);
      }
    }
  };

  const handleConfirmAddCustomName = (name: string) => {
    onAddCustomWelderName(name);
    setWelderName(name);
    setWelderId(null);
    setOperatorUserId(null);
  };

  const setQualForWps = (wpsId: string, value: string) => {
    setQualifications((prev) => ({ ...prev, [wpsId]: value }));
  };

  const handleSubmit = async () => {
    const wn = weldNumber.trim();
    const welder = welderName.trim();
    if (!wn) {
      customNotification.error('Validation', 'Enter a weld number.');
      return;
    }
    if (!welder) {
      customNotification.error('Validation', 'Select or enter a welder name.');
      return;
    }
    const duplicateWelder = qualificationList.find((r) => {
      if (isEditMode && welderRowToEdit && String(r.id) === String(welderRowToEdit.id)) return false;
      const leftId = r.welderId != null ? Number(r.welderId) : null;
      const rightId = welderId != null ? Number(welderId) : null;
      if (leftId != null && rightId != null) {
        return leftId === rightId && r.welderName.trim().toLowerCase() === welder.toLowerCase();
      }
      return r.welderName.trim().toLowerCase() === welder.toLowerCase();
    });
    if (duplicateWelder) {
      customNotification.error(
        'Validation',
        'This welder is already added in the register. Please edit the existing row instead of adding duplicate.'
      );
      return;
    }

    const pipeLabels = pipeWpsRows
      .map((r) => (r.pipeSizeDisplay || '').trim().toLowerCase())
      .filter(Boolean);
    const dupPipe = pipeLabels.find((lab, idx) => pipeLabels.indexOf(lab) !== idx);
    if (dupPipe) {
      customNotification.error('Validation', 'Each line must use a different pipe size.');
      return;
    }

    for (let i = 0; i < pipeWpsRows.length; i++) {
      const r = pipeWpsRows[i];
      const hasWps = (r.wpsSpecificationLabel || '').trim().length > 0;
      const hasPipe = (r.pipeSizeDisplay || '').trim().length > 0;
      const hasScope = (r.scopeOfWorkType || '').trim().length > 0;
      const hasWork = (r.workType || '').trim().length > 0;
      if (hasWps && !hasPipe) {
        customNotification.error(
          'Validation',
          `Line ${i + 1}: select a pipe size before choosing a WPS, or clear the WPS.`
        );
        return;
      }
      if ((hasPipe || hasWps) && (!hasScope || !hasWork)) {
        customNotification.error(
          'Validation',
          `Line ${i + 1}: select scope of work and work type (or clear pipe and WPS for that line).`
        );
        return;
      }
    }

    const quals: Record<string, string> = { ...qualifications };
    wpsColumns.forEach((c) => {
      if (quals[c.id] === undefined) quals[c.id] = '-';
    });

    let newUploadDocs: WelderQualificationRowDocument[] = [];
    if (materialDocuments.length > 0) {
      setIsUploading(true);
      try {
        const projectTypeId =
          Number((project as any)?.project?.projectTypeId) ||
          Number((project as any)?.projectTypeId) ||
          1;
        const payload = {
          pipeId: 0,
          projectId: Number(projectId),
          projectTypeId,
          type: 'EQUIPMENT',
        };
        const s3Response = await getPreSignedUrlToUpload(materialDocuments, payload, 1);
        if (!s3Response || s3Response.length === 0) {
          customNotification.error('Upload Failed', 'Could not upload documents. Please try again.');
          return;
        }
        newUploadDocs = s3Response.map((file: { fileName: string; s3Key: string }, index: number) => ({
          fileName: file.fileName,
          s3Key: file.s3Key,
          tags: selectedTags[index] || [],
          uploadedBy: user.email || '',
        }));
      } catch {
        customNotification.error('Upload Failed', 'An error occurred while uploading documents.');
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const mergedDocsRaw = isEditMode ? [...existingDocuments, ...newUploadDocs] : newUploadDocs;
    const documents = mergedDocsRaw.length > 0 ? mergedDocsRaw : undefined;

    const pipeWpsLines: WelderPipeWpsLine[] = pipeWpsRows
      .map((r) => ({
        pipeSizeDisplay: (r.pipeSizeDisplay || '').trim() || undefined,
        pipeId: r.pipeId,
        projectTypeId: r.projectTypeId,
        wpsSpecificationId: r.wpsSpecificationId,
        wpsSpecificationLabel: (r.wpsSpecificationLabel || '').trim() || null,
        qualificationType: null,
        scopeOfWorkType: (r.scopeOfWorkType || '').trim() || null,
        workType: (r.workType || '').trim() || null,
      }))
      .filter(
        (l) =>
          l.pipeSizeDisplay ||
          l.wpsSpecificationLabel ||
          (l.scopeOfWorkType != null && l.scopeOfWorkType !== '') ||
          (l.workType != null && l.workType !== '')
      );

    const pipeSumm = pipeWpsLines.map((l) => l.pipeSizeDisplay).filter(Boolean).join('; ') || undefined;
    const wpsSumm =
      pipeWpsLines.map((l) => l.wpsSpecificationLabel).filter(Boolean).join('; ') || null;
    const first = pipeWpsLines[0];

    const payload: any = {
      project_id: Number(project.project.projectId),
      weldNumber: wn,
      welderName: welder,
      welderId: welderId ? welderId.toString() : null,
      operatorUserId,
      notes: notes.trim() || undefined,
      documents,
      qualifications: quals,
      pipeWpsLines: pipeWpsLines.length > 0 ? pipeWpsLines : undefined,
      pipeSizeDisplay: pipeSumm,
      pipeId: first?.pipeId ?? null,
      projectTypeId: first?.projectTypeId ?? null,
      wpsSpecificationId: first?.wpsSpecificationId ?? null,
      wpsSpecificationLabel: wpsSumm,
    };
    if(isEditMode && welderRowToEdit) {
      payload.id = welderRowToEdit.id;
      payload.createdBy= user.email || null;
    }

    const bulkItem =  {
      items: [
        payload
      ]
    };

    setIsSaving(true);
    try {
      const result = await bulkCreateUpdateWelderQualification(bulkItem);
      if (result != null) {
        await onSaved?.();
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsUploading(false);
    setIsSaving(false);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="w-[90vw] max-w-[1500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {isEditMode ? 'Edit' : 'Add'} Welder Qualification
            </DialogTitle>
          </DialogHeader>

          <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">
            <div className="space-y-2">
              <Label htmlFor="wq-weld-number">
                Weld number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="wq-weld-number"
                value={weldNumber}
                onChange={(e) => setWeldNumber(e.target.value)}
                placeholder="e.g. 8108"
                className="w-full"
              />
            </div>

            <div className="space-y-2 col-span-3">
              <Label>
                Welder name <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={welderName.trim() || undefined}
                  onValueChange={handleWelderSelect}
                  placeholder="Select welder (team)…"
                  searchPlaceholder="Search welder name…"
                  options={welderNameOptions}
                  showAddButton
                  onAddNew={() => setIsAddingWelderName(true)}
                  addNewText="Add New Welder Name"
                  disabled={!projectId}
                  loading={teamMembersLoading}
                  loadingText="Loading team…"
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2 col-span-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <Label className="text-base font-medium">Pipe size &amp; WPS</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    From project setup and specifications. Add multiple lines like QC qualifications.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleAddPipeWpsRow}
                  size="sm"
                //  variant="outline"
                  className="flex items-center gap-2 shrink-0"
                  disabled={!projectId}
                >
                  <Plus className="h-4 w-4" />
                  Add pipe / WPS line
                </Button>
              </div>

              <div className="space-y-4 border rounded-lg p-4">
                {pipeWpsRows.map((row, index) => {
                  const wpsOpts = buildWpsOptionsForPipe(row.pipeId);
                  const pipeLabel = (row.pipeSizeDisplay || '').trim();
                  return (
                    <div
                      key={row.key}
                      className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 p-4 border rounded-lg bg-gray-50"
                    >
                      <div className="space-y-2">
                        <Label>
                          Pipe size <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative mt-1">
                          <SearchableSelect
                            value={pipeLabel || undefined}
                            onValueChange={(label) => handlePipeSizeSelectAt(index, label)}
                            placeholder={pipeSizeOptions.length ? 'Select pipe size…' : 'No pipe sizes in setup'}
                            searchPlaceholder="Search pipe sizes…"
                            options={pipeSizeOptions}
                            showAddButton={false}
                            disabled={!projectId || pipeSizeOptions.length === 0}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>WPS number (specification)</Label>
                        <div className="relative mt-1">
                          <SearchableSelect
                            value={(row.wpsSpecificationLabel || '').trim() || undefined}
                            onValueChange={(name) => handleWpsSpecSelectAt(index, row.pipeId, name)}
                            placeholder={
                              wpsOpts.length ? 'Select WPS…' : 'No WPS specifications found'
                            }
                            searchPlaceholder="Search WPS…"
                            options={wpsOpts}
                            showAddButton={false}
                            disabled={!projectId || wpsOpts.length === 0}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>
                          Scope of work <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative mt-1">
                          <SearchableSelect
                            value={(row.scopeOfWorkType || '').trim() || undefined}
                            onValueChange={(value) => updatePipeWpsRow(index, { scopeOfWorkType: value })}
                            placeholder="Select scope of work"
                            searchPlaceholder="Search scope…"
                            options={scopeOfWorkTypeOptions}
                            showAddButton
                            onAddNew={() => setIsAddingScopeOfWorkType(true)}
                            addNewText="Add New Scope of Work Type"
                            disabled={!projectId}
                            className="w-full"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Work type <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative mt-1">
                          <SearchableSelect
                            value={(row.workType || '').trim() || undefined}
                            onValueChange={(value) => updatePipeWpsRow(index, { workType: value })}
                            placeholder="Select work type"
                            searchPlaceholder="Search work type…"
                            options={workTypeOptions}
                            showAddButton
                            onAddNew={() => setIsAddingWorkType(true)}
                            addNewText="Add New Work Type"
                            disabled={!projectId}
                            className="w-full"
                          />
                        </div>
                      </div>

                      {pipeWpsRows.length > 1 && (
                        <div className="lg:col-span-2 xl:col-span-4 flex justify-end">
                          <Button
                            type="button"
                            onClick={() => handleRemovePipeWpsRow(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                           
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 col-span-4">
              <Label className="text-base font-medium">Notes</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-400"
                placeholder="Enter notes..."
              />
            </div>

            {existingDocuments.length > 0 && (
              <div className="space-y-2 col-span-4">
                <Label className="text-base font-medium text-gray-800">Existing documents</Label>
                <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                  {existingDocuments.map((d, i) => (
                    <li key={`${d.s3Key}-${i}`} className="break-all">
                      {d.fileName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-2 col-span-4">
              <Label>Upload Documents</Label>
              <p className="text-sm text-gray-600">
                {isEditMode
                  ? 'Add more welder qualification and WPS supporting documents.'
                  : 'Upload welder qualification and WPS supporting documents.'}
                <br />
                <span className="text-xs text-gray-500">
                  Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                  <br />
                  Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                </span>
              </p>

              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple={true}
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags={true}
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                showAddButton={true}
                addButtonText="Add New Tag"
                onAddButtonClick={() => setIsAddingTag(true)}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />

              <div className="text-xs text-gray-500">
                Current files: {materialDocuments.length} file(s)
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex items-center gap-2"
              disabled={isUploading || isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="flex items-center gap-2 bg-[#e04530] text-white hover:bg-[#e04530]/90"
              onClick={() => void handleSubmit()}
              disabled={weldNumber.trim() === '' || welderName.trim() === '' || isUploading || isSaving}
            >
              <Plus className="h-4 w-4" />
              {isUploading || isSaving ? 'Saving...' : isEditMode ? 'Update Welder Row' : 'Add Welder Row'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={(name) => void handleAddTag(name)}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingWelderName}
        onOpenChange={setIsAddingWelderName}
        title="Add New Welder Name"
        label="Name"
        placeholder="Full name as on certificate"
        onAdd={handleConfirmAddCustomName}
        buttonText="Add"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingScopeOfWorkType}
        onOpenChange={setIsAddingScopeOfWorkType}
        title="Add New Scope of Work Type"
        label="Type name"
        placeholder="e.g. BA — Buckle Arrestor"
        onAdd={(name) => void handleAddScopeOfWorkType(name)}
        buttonText="Add Type"
        cancelText="Cancel"
      />
      <AddNewItemDialog
        open={isAddingWorkType}
        onOpenChange={setIsAddingWorkType}
        title="Add New Work Type"
        label="Type name"
        placeholder="e.g. Field welding"
        onAdd={(name) => void handleAddWorkType(name)}
        buttonText="Add Type"
        cancelText="Cancel"
      />
    </>
  );
};
